"use client"

import { CameraOutlined, EditOutlined } from "@ant-design/icons"
import { Button, Form, Input, Modal } from "antd"
import { t } from "i18next"
import React, { useEffect, useState } from "react"
import ReactQuill from "react-quill"
import { BlockContentNavigator } from "../../../../(admin)/components/block-content-navigator"
import { ADMIN_API_CONFIG } from "../../../../constants/api-configs"
import CKEditorWrapper from "../../../../lib/config/lib/CkeditorWrapper"
import { PageFormProps } from "../../../../lib/config/types"
import { slugPattern } from "../../../../utils/validationPatterns"
import AddNewMedia from "../../media/pages/AddNewMedia"
import Media from "../../media/pages/Media"
import { FieldType, MediaData, PageData } from "../../ui-builder/config/types"
import pageService from "../services/page"

const removeAccents = (str: string) => {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[đĐ]/g, "d")
    .replace(/[âÂ]/g, "a")
    .replace(/[êÊ]/g, "e")
    .replace(/[.,:"'<>?`!@#$%^&*();/\\]/g, "")
    .replace(/\s+/g, "-")
    .toLowerCase()
}

const useBlockContent = (page: PageData | null) => {
  const [isUseBlockContent, setIsUseBlockContent] = useState(false)
  const [blockContentId, setBlockContentId] = useState<any>(null)

  React.useEffect(() => {
    if (page?.attributes?.blockContent?.data || page?.attributes?.isUseUIBuilder) {
      setIsUseBlockContent(true)
      setBlockContentId(page.attributes.blockContent.data?.id)
    }
  }, [page])

  return { isUseBlockContent, blockContentId }
}

const PageForm: React.FC<PageFormProps> = ({
  page,
  edit,
  form,
  onFinishFailed,
  quillValue,
  setQuillValue,
  onFinish,
  onChangeMetaImage,
  isUIBuilderPage,
}) => {
  const quillRef = React.useRef<ReactQuill>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isChooseMedia, setIsChooseMedia] = useState(true)
  const [selectedMetaImage, setSelectedMetaImage] = useState<string | null>(null)
  const [modalMetaImage, setModalMetaImage] = useState(false)
  const [isEditableSlug, setIsEditableSlug] = useState(true)
  const pageServices = pageService()

  const handleSubmit = () => {
    const editorContent = quillRef.current?.getEditor().root.innerHTML || ""
    form.setFieldsValue({ content: editorContent })
    onFinish(form.getFieldsValue())
  }

  useEffect(() => {
    if (quillValue && quillRef.current) {
      const editor = quillRef.current.getEditor()
      editor.root.innerHTML = quillValue
    }
  }, [quillValue])

  const handleOpenModal = (modalMetaImage: boolean) => {
    setModalMetaImage(modalMetaImage)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const handleSelectMedia = (media: MediaData) => {
    const quillEditor = quillRef.current?.getEditor()

    if (quillEditor) {
      const range = quillEditor.getSelection(true)
      quillEditor.insertEmbed(range.index, "image", `${ADMIN_API_CONFIG.API_URL}${media.url}`)
    }

    setIsModalOpen(false)
  }

  const handleNameBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const nameValue = e.target.value.trim()
    if (!nameValue) {
      form.setFields([{ name: "slug", errors: [] }])
      return
    }

    const slugValue = removeAccents(nameValue)

    const isSlugUnique = async (slug: string): Promise<boolean> => {
      try {
        const response = await pageServices.checkSlug(slug)

        return response.data.length === 0
      } catch {
        return false
      }
    }

    const isUnique = await isSlugUnique(slugValue)
    if (!isUnique) {
      form.setFieldsValue({ slug: slugValue })
      form.setFields([{ name: "slug", errors: [t("Slug already exists. Please choose another slug.")] }])
    } else {
      form.setFieldsValue({ slug: slugValue })
      form.setFields([{ name: "slug", errors: [] }])
    }
  }
  const isValidJson = (str: string): boolean => {
    try {
      JSON.parse(str)
    } catch (e) {
      return false
    }
    return true
  }

  const handleSelectMetaImage = (media: MediaData) => {
    setSelectedMetaImage(`${ADMIN_API_CONFIG.API_URL}${media.url}`)
    onChangeMetaImage(media)
    setIsModalOpen(false)
  }

  // @ts-expect-error
  const { isUseBlockContent, blockContentId } = useBlockContent(page)

  return (
    <Form
      form={form}
      className="flex flex-col w-[77%] "
      name="basic"
      initialValues={{ remember: true }}
      onFinish={handleSubmit}
      onFinishFailed={onFinishFailed}
      autoComplete="off"
      layout="vertical"
    >
      <Form.Item<FieldType> name="name" rules={[{ required: true, message: t("Please enter a page name!") }]}>
        <Input
          placeholder={t("Add Name")}
          className="text-xl custom-input mt-4"
          onBlur={handleNameBlur}
          onChange={e => {
            form.setFieldValue("metaTitle", e.target.value)
            if (form.getFieldValue("excerpt") == undefined) {
              form.setFieldValue("metaDescription", e.target.value)
            }
          }}
        />
      </Form.Item>

      <div className="flex w-full items-center pb-1">
        <Form.Item<FieldType>
          className="w-1/2 mr-4"
          name="slug"
          rules={[
            {
              pattern: slugPattern,
              message: t("Slug must match the following: 'A-Z, a-z, 0-9, - ,_ ,. ,~'"),
            },
          ]}
          label={<span className="text-2xl font-medium pb-2">{t("Slug")}</span>}
          extra={<span className="text-sm ">{t("May not need to be entered (automatically render by name)")}</span>}
        >
          <Input disabled={isEditableSlug} placeholder={t("Slug")} className="custom-input" />
        </Form.Item>
        <Button
          className="self-center"
          icon={<EditOutlined />}
          color={!isEditableSlug ? "primary" : "default"}
          variant="outlined"
          onClick={() => setIsEditableSlug(!isEditableSlug)}
        >
          {t("Edit")}
        </Button>
      </div>

      {isUseBlockContent || isUIBuilderPage ? (
        <BlockContentNavigator blockContentId={blockContentId} edit={!!edit} />
      ) : (
        <>
          <Form.Item>
            <Button icon={<CameraOutlined />} onClick={() => handleOpenModal(false)}>
              {t("Add media")}
            </Button>
          </Form.Item>

          <Form.Item<FieldType> name="content">
            <div
              style={{
                height: "500px",
                overflowY: "auto",
                position: "relative",
              }}
            >
              <CKEditorWrapper
                value={form.getFieldValue("content")}
                onChange={(data: string) => {
                  form.setFieldsValue({ content: data })
                }}
              />
            </div>
          </Form.Item>

          <Modal
            open={isModalOpen}
            title={<span className="ml-4">{t("Select Media")}</span>}
            onCancel={handleCloseModal}
            style={{ top: 20 }}
            width="95%"
            footer={null}
          >
            <div className="ml-4 mt-5">
              <Button
                onClick={() => setIsChooseMedia(true)}
                className="mr-2"
                style={{
                  backgroundColor: isChooseMedia ? "blue" : "initial",
                  color: isChooseMedia ? "white" : "initial",
                }}
              >
                {t("Select Media")}
              </Button>
              <Button
                onClick={() => setIsChooseMedia(false)}
                style={{
                  backgroundColor: !isChooseMedia ? "blue" : "initial",
                  color: !isChooseMedia ? "white" : "initial",
                }}
              >
                {t("Upload Media")}
              </Button>
            </div>
            <div>
              {isChooseMedia ? (
                <Media
                  isOpenModal={true}
                  onSelectMedia={media => {
                    modalMetaImage ? handleSelectMetaImage(media) : handleSelectMedia(media)
                  }}
                />
              ) : (
                <AddNewMedia isOpenModal={true} setChooseMedia={setIsChooseMedia} />
              )}
            </div>
          </Modal>
        </>
      )}
      <span className="text-base font-medium pt-2 pb-2">{t("SEO")}</span>

      <div className="grid grid-cols-2 gap-x-5 gap-y-2 p-5 border rounded-[10px]">
        <Form.Item<FieldType>
          name="metaTitle"
          label={<span className="text-base font-medium p">{t("Meta Title")}</span>}
        >
          <Input className="custom-input py-2" />
        </Form.Item>

        <Form.Item<FieldType>
          name="keywords"
          label={<span className="text-base font-medium">{t("Meta Keywords")}</span>}
        >
          <Input className="custom-input py-2" />
        </Form.Item>

        <Form.Item<FieldType>
          name="metaDescription"
          label={<span className="text-base font-medium">{t("Meta Description")}</span>}
        >
          <Input.TextArea className="custom-input py-2" rows={5} />
        </Form.Item>

        <Form.Item<FieldType>
          name="structuredData"
          label={<span className="text-base font-medium">{t("Structured Data")}</span>}
          rules={[
            {
              validator: (_, value) => {
                if (value && !isValidJson(value)) {
                  return Promise.reject(new Error(t("Invalid JSON format!")))
                }
                return Promise.resolve()
              },
            },
          ]}
        >
          <Input.TextArea className="custom-input py-2" rows={5} placeholder={t('@example: {"id": "string"}')} />
        </Form.Item>

        <Form.Item<FieldType>
          name="metaRobots"
          label={<span className="text-base font-medium">{t("Meta Robots")}</span>}
        >
          <Input className="custom-input py-2" />
        </Form.Item>

        <Form.Item<FieldType>
          name="metaViewport"
          label={<span className="text-base font-medium">{t("Meta Viewport")}</span>}
        >
          <Input className="custom-input py-2" />
        </Form.Item>

        <Form.Item<FieldType>
          name="canonicalURL"
          label={<span className="text-base font-medium">{t("Canonical URL")}</span>}
        >
          <Input className="custom-input py-2" />
        </Form.Item>
      </div>
    </Form>
  )
}

export default PageForm
